import { AccordionDisclosure } from "theme";
import { EffectCarousel } from "theme";

class AccordionDisclosureGroup extends HTMLElement {
  constructor() {
    super(); // always call super() first in the constructor. This also calls the extended class' constructor.
    this.accordions = this.querySelectorAll('details');
  }
  connectedCallback() {
    this.accordions.forEach((details) => {
      details.addEventListener("click", this.closeOthers.bind(this), true);
    });
  }
  disconnectedCallback() {
    if (this.accordions) {
      this.accordions.forEach((details) => {
        details.removeEventListener("click", this.closeOthers.bind(this), true);
      });
    }
  }
  closeOthers(e) {
    const clickedDetails = e.target.closest('details');
    this.accordions.forEach((details) => {
      if (details !== clickedDetails) {
        details.removeAttribute('open');
        details.setAttribute('aria-expanded', 'false');
      }
    });
  }
};
customElements.define("accordion-group", AccordionDisclosureGroup);


class SurfaceSelector extends HTMLElement {
  constructor() {
    super();
    this._onChange = this._onChange.bind(this);
    const script = this.querySelector("script[data-variant-recommendation]");
    let variants = [];
    if (script && script.textContent) {
      try {
        const parsed = JSON.parse(script.textContent);
        variants = Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        console.error("Invalid JSON in data-variant-recommendation script:", e);
        variants = [];
      }
    }
    this._variantAvailable = Array.isArray(variants) ? variants : [];
  }

  connectedCallback() {
    // Attach event listeners to all select elements inside this component
    this._attachListeners();
  }

  disconnectedCallback() {
    this._detachListeners();
  }

  _attachListeners() {
    this._selects = this.querySelectorAll('select');
    this._selects.forEach(select => {
      select.addEventListener('change', this._onChange);
    });
    this.addEventListener('variant:change', this._onVariantChange);
  }

  _detachListeners() {
    if (this._selects) {
      this._selects.forEach(select => {
        select.removeEventListener('change', this._onChange);
      });
    }
  }
  _onVariantChange(event) {
    // Reset all selects to default value and enable all options
    this._selects.forEach(select => {
      select.value = '';
      select.removeAttribute('disabled');
      Array.from(select.options).forEach(option => {
        option.disabled = false;
      });
    });
  }
  _onChange(event) {
    // Loop through this._variantAvailable and collect available sheen parameters
    const selectedSurface = event.target.value;
    // Collect all unique sheens for variants that include the selected surface
    const availableSheens = Array.from(
      new Set(
        this._variantAvailable
          .filter(variant => Array.isArray(variant.surface) && variant.surface.includes(selectedSurface))
          .flatMap(variant => Array.isArray(variant.sheen) ? variant.sheen : [])
          .filter(sheen => sheen !== undefined && sheen !== null && sheen !== '')
      )
    );
    // Custom event for parent listeners, e.g. for form or product updates
    this._selects.forEach(select => {
      if (select !== event.target) {
        select.removeAttribute('disabled');
        Array.from(select.options).forEach(option => {
          if (option.value === '') {
            // Always enable the default empty option
            option.disabled = false;
          } else if (select.name.toLowerCase().includes('sheen')) {
            // Enable or disable sheen options based on availableSheens
            option.disabled = !availableSheens.includes(option.value);
          } else {
            // For other selects, you can implement similar logic if needed
            option.disabled = false; // Or some other condition
          }
          // If the currently selected option is now disabled, reset to default
          if (option.selected && option.disabled) {
            select.value = '';
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }
    });


    // If both selectors have non-default values, find recommended variant(s)
    const selectedValues = Array.from(this._selects)
      .map(select => select.value)
      .filter(value => value !== '');

    if (selectedValues.length === this._selects.length) {
      // Both selectors have non-default values
      const recommendedVariants = this._variantAvailable.filter(variant => {
        // Check if variant matches all selected values
        return selectedValues.every(value =>
          (Array.isArray(variant.surface) && variant.surface.includes(value)) ||
          (Array.isArray(variant.sheen) && variant.sheen.includes(value))
        );
      });
      // You can use recommendedVariants as needed, e.g. display, select, etc.
      // If there is an input element with the same value as a recommended variant, select it
      for (const variant of recommendedVariants) {
        const valueToMatch = String(variant.id);
        const labels = this.closest('quick-order-list')?.querySelectorAll('label[data-variant-id]');
        if (!labels) continue;

        for (const label of labels) {
          const variantIds = label.dataset.variantId.split(',').map(id => id.trim());

          if (variantIds.includes(valueToMatch)) {
            label.classList.add('selected');

            const input = label.parentElement.querySelector('input[type="radio"], input[type="checkbox"]');

            if (input) {
              input.dataset.triggeredBy = 'surface-selector';
              setTimeout(() => {
                input.click();
                input.checked = true;
              }, 200);
            }

            // Stop both loops after first match
            return;
          } else {
            label.classList.remove('selected');
          }
        }
      }
    } else {
      // If not all selectors have values, remove 'selected' class from all labels
      const labels = this.closest('quick-order-list')?.querySelectorAll('label[data-variant-id]');
      if (labels) {
        labels.forEach(label => {
          const input = label.parentElement.querySelector('input[type="radio"], input[type="checkbox"]');
          label.classList.remove('selected');
          if (input) {
            input.checked = false;
            const sizeAccordion = document.querySelector('.accordion--size');
            if (sizeAccordion) {
              sizeAccordion.removeAttribute('open');
              sizeAccordion.setAttribute('disabled', '');
            }

            const selectedOption = document.querySelector('selected-option');
            if (selectedOption) {
              const span = selectedOption.querySelector('span');
              const input = selectedOption.querySelector('input');
              
              if (span) span.innerHTML = 'Select finish';
              if (input) input.checked = false;
            }
          }
        });
      }
    }
    this.dispatchEvent(new CustomEvent('surface-selector:change', {
      detail: {
        name: event.target.name,
        value: event.target.value
      },
      bubbles: true
    }));
  }
}

customElements.define('surface-selector', SurfaceSelector);
// reset the paint calculate when user select non recommended variant options


/* -------------------------------------------
 * <quick-order-list-submit>
 * ----------------------------------------- */

class QuickOrderListSubmit extends HTMLElement {
  static get observedAttributes() {
    return ['include-zero']; // optional flag to send zeroes too
  }

  connectedCallback() {
    this.addEventListener('click', this.onClick);
    this.setAttribute('role', this.getAttribute('role') || 'button');
    this.tabIndex ||= 0;
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.click();
      }
    });

    // Listen for changes to quantity inputs to update total price
    this._bindQuantityChange();
    this._updateTotalPrice();

    // Listen for variant:change to reinitialize
    document.addEventListener('variant:change', this._onVariantChange);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this._unbindQuantityChange();
    document.removeEventListener('variant:change', this._onVariantChange);
  }

  _bindQuantityChange() {
    const root = this.closest('quick-order-list') || document.querySelector('quick-order-list') || document;
    this._quantityInputs = Array.from(
      root.querySelectorAll('quick-order-list-quantity-selector[variant-id] input[type="number"], quick-order-list-quantity-selector[variant-id] input[inputmode="numeric"]')
    );
    this._quantityInputs.forEach(input => {
      input.addEventListener('input', this._updateTotalPrice.bind(this));
      input.addEventListener('change', this._updateTotalPrice.bind(this));
    });
  }

  _unbindQuantityChange() {
    if (this._quantityInputs) {
      this._quantityInputs.forEach(input => {
        input.removeEventListener('input', this._updateTotalPrice);
        input.removeEventListener('change', this._updateTotalPrice);
      });
    }
  }

  _updateTotalPrice = () => {
    const root = document;
    const selectors = Array.from(
      root.querySelectorAll('quick-order-list-quantity-selector[variant-id]')
    );
    let total = 0;
    selectors.forEach(el => {
      const input =
        el.querySelector('.quantity-selector__input-wrapper input') ||
        el.querySelector('input[type="number"], input[inputmode="numeric"]');
      const qty = Number(input?.value ?? 0);
      const price = Number(input.getAttribute('data-price') || 0); // expects price in cents
      if (Number.isFinite(qty) && Number.isFinite(price)) {
        total += qty * price;
      }
    });

    // Display total price (expects an element with class .quick-order-list-total)
    const totalEls = root.querySelectorAll('.quick-order-list-total');
    totalEls.forEach(totalEl => {
      // Format price as Shopify money (assumes cents)
      totalEl.textContent = new Intl.NumberFormat(Shopify.locale, { style: "currency", currency: Shopify.currency.active }).format(total / 100);
      if (total == 0) {
        this.classList.add('hidden');
        if (totalEl.closest('price-list')) {
          totalEl.closest('price-list').classList.add('hidden');
        }
        if (totalEl.closest('.product-info__block-item')) {
          totalEl.closest('.product-info__block-item').querySelector('quick-order-list-submit').classList.add('hidden');
        }
      } else {
        this.classList.remove('hidden');
        if (totalEl.closest('price-list')) {
          totalEl.closest('price-list').classList.remove('hidden');
        }
        document.querySelectorAll('quick-order-list-submit').forEach(btn => btn.classList.remove('hidden'));
      }
    });
  };

  onClick = () => {
    const root = this.closest('quick-order-list') || document.querySelector('quick-order-list') || document;
    const selectors = Array.from(
      root.querySelectorAll('quick-order-list-quantity-selector[variant-id]')
    );
    const includeZero = this.hasAttribute('include-zero');
    const updates = {};
    for (const el of selectors) {
      const variantId = el.getAttribute('variant-id');
      const input =
        el.querySelector('.quantity-selector__input-wrapper input') ||
        el.querySelector('input[type="number"], input[inputmode="numeric"]');
      const qty = Number(input?.value ?? 0);
      if (!Number.isFinite(qty) || !variantId) continue;

      if (includeZero || qty > 0) {
        updates[variantId] = qty;
      }
    }

    if (!Object.keys(updates).length) {
      this.setAttribute('aria-disabled', 'true');
      setTimeout(() => this.removeAttribute('aria-disabled'), 150);
      return;
    }

    this.setAttribute('aria-busy', 'true');

    // Try to find the quick-order-list element and dispatch the event on it
    // This ensures the event reaches the right listeners even when DOM position changes
    const quickOrderList = root.querySelector('quick-order-list') || root.closest('quick-order-list');
    const eventTarget = quickOrderList || root || document;
    eventTarget.dispatchEvent(
      new CustomEvent('quick-order-list:update', {
        bubbles: true,
        composed: true,
        detail: { updates },
      })
    );

    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      cartDrawer.show();
      cartDrawer.classList.add('open');
    }

    setTimeout(() => this.removeAttribute('aria-busy'), 800);
    // Reset all quantities to zero after the drawer is opened
    this._quantityInputs = Array.from(
      root.querySelectorAll('quick-order-list-quantity-selector[variant-id] input[type="number"], quick-order-list-quantity-selector[variant-id] input[inputmode="numeric"]')
    );
    if (cartDrawer) {
      setTimeout(() => {
        this._quantityInputs.forEach(input => {
          input.value = 0;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }, 2000);
    }
  };

  _onVariantChange = () => {
    // Unbind previous listeners and re-bind everything
    this._unbindQuantityChange();
    this._bindQuantityChange();
    this._updateTotalPrice();
  };
}

if (!customElements.get('quick-order-list-submit')) {
  customElements.define('quick-order-list-submit', QuickOrderListSubmit);
}



var AccordionDisclosureDrawer = class extends AccordionDisclosure {
  static get observedAttributes() {
    return ['disabled'];
  }

  constructor() {
    super();
    this._onSummaryClick = this._onSummaryClick.bind(this);
    this._onSummaryKeydown = this._onSummaryKeydown.bind(this);
    this._onContentChange = this._onContentChange.bind(this);
    this._onScrollDown = this._onScrollDown.bind(this);
  }

  connectedCallback() {
    super.connectedCallback && super.connectedCallback();

    // Initial update
    this._updateSelectedOption();

    // Listen for changes on radio inputs inside accordion__content
    const content = this.querySelector('.accordion__content');
    if (content) {
      content.addEventListener('change', this._onContentChange);
    }

    // Add click listener to selected-option to open accordion
    const selectedOptionEl = this.querySelector('selected-option');
    if (selectedOptionEl) {
      selectedOptionEl.addEventListener('click', () => {
        if (!this.hasAttribute('disabled')) {
          this.open && this.open();
        }
      });
    }

    // Intercept clicks/keys on the summary to prevent native toggle when disabled
    this._summary = this.querySelector('summary, .accordion__summary');
    if (this._summary) {
      this._summary.addEventListener('click', this._onSummaryClick, true);
      this._summary.addEventListener('keydown', this._onSummaryKeydown, true);
    }

    // Apply initial disabled state if present
    if (this.hasAttribute('disabled')) this._applyDisabledState();
    if (this.classList.contains('accordion--finish')) {
      document.addEventListener('dialog:body-scroll', this._onScrollDown);
    }
  }

  disconnectedCallback() {
    const content = this.querySelector('.accordion__content');
    if (content) {
      content.removeEventListener('change', this._onContentChange);
    }

    const selectedOptionEl = this.querySelector('selected-option');
    if (selectedOptionEl) {
      selectedOptionEl.removeEventListener('click', () => { });
    }

    if (this._summary) {
      this._summary.removeEventListener('click', this._onSummaryClick, true);
      this._summary.removeEventListener('keydown', this._onSummaryKeydown, true);
    }

    super.disconnectedCallback && super.disconnectedCallback();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'disabled') {
      if (this.hasAttribute('disabled')) {
        this._applyDisabledState();
      } else {
        this._removeDisabledState();
      }
    }
  }
  _onScrollDown(e) {
    const detail = e?.detail ?? {};
    const scrollY = Number(detail.scrollY ?? detail.scrollTop ?? detail.y ?? window.pageYOffset ?? document.documentElement?.scrollTop ?? 0);
    const threshold = 5; // pixels to ignore tiny movements

    if (typeof this._lastScrollY === 'undefined') this._lastScrollY = scrollY;

    const isScrollDown = scrollY - this._lastScrollY > threshold;
    this._lastScrollY = scrollY;
    if (isScrollDown && !this.hasAttribute('disabled')) {
      super.close && super.close();
    }
  }
  _onContentChange(e) {
    if (e.target.matches('input[type="radio"]')) {
      this._updateSelectedOption();
    }
  }

  _onSummaryClick(e) {
    if (this.hasAttribute('disabled')) {
      // Prevent native details toggle
      e.preventDefault();
      e.stopImmediatePropagation();
      // Ensure it is closed and marked as not expanded
      this.removeAttribute('open');
      this.setAttribute('aria-expanded', 'false');
    }
  }

  _onSummaryKeydown(e) {
    if (!this.hasAttribute('disabled')) return;
    // Prevent Enter/Space from toggling when disabled
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopImmediatePropagation();
    }
  }

  _applyDisabledState() {
    this.setAttribute('aria-disabled', 'true');
    // ensure closed
    this.removeAttribute('open');
    this.setAttribute('aria-expanded', 'false');
    if (this._summary) {
      // Make summary unfocusable
      this._previousTabIndex = this._summary.getAttribute('tabindex');
      this._summary.setAttribute('tabindex', '-1');
      this._summary.classList.add('is-disabled');
    }
  }

  _removeDisabledState() {
    this.removeAttribute('aria-disabled');
    if (this._summary) {
      if (this._previousTabIndex !== null && this._previousTabIndex !== undefined) {
        this._summary.setAttribute('tabindex', this._previousTabIndex);
      } else {
        this._summary.removeAttribute('tabindex');
      }
      this._summary.classList.remove('is-disabled');
    }
  }

  // Prevent programmatic open/close when disabled
  open() {
    if (this.hasAttribute('disabled')) return;
    return super.open && super.open();
  }

  close() {
    if (this.hasAttribute('disabled')) return;
    return super.close && super.close();
  }

  _updateSelectedOption() {
    const content = this.querySelector('.accordion__content');
    const selectedOptionEl = this.querySelector('selected-option span');
    if (!content || !selectedOptionEl) return;

    // Find checked radio input inside accordion__content
    const checkedInput = content.querySelector('input[type="radio"]:checked');
    if (checkedInput) {
      // Find the label associated with the checked input
      const label = content.querySelector(`label[for="${checkedInput.id}"] span.heading`);
      if (label) {
        // Clone the label and replace selected-option's content
        selectedOptionEl.innerHTML = '';
        selectedOptionEl.appendChild(label.cloneNode(true));
      } else {
        // If no label, just show the value
        selectedOptionEl.textContent = checkedInput.value;
      }
      selectedOptionEl.setAttribute('aria-hidden', 'false');
      selectedOptionEl.closest('.checkbox-control').querySelector('input[type="checkbox"]').checked = true;
    } else {
      // No selection
      selectedOptionEl.textContent = 'Select finish';
      selectedOptionEl.setAttribute('aria-hidden', 'true');
    }
  }
};

if (!window.customElements.get("accordion-disclosure-drawer")) {
  window.customElements.define("accordion-disclosure-drawer", AccordionDisclosureDrawer);
}


document.addEventListener('DOMContentLoaded', () => {
  const finishSelector = document.querySelector('#finish-box');
  const finishDrawer = document.querySelector('#finish-drawer');
  const finishField = document.querySelector('#selected-finish');
  const finishFieldI = document.querySelector('#finish-boxI');


  const finishTypesSelector = document.querySelector('.different-finish-types');
  const finishTypesDrawer = document.querySelector('#finishtypes-drawer');

  if (finishTypesSelector && finishTypesDrawer) { 
    finishTypesSelector.addEventListener('click', () => {
      finishTypesDrawer.show();
      finishTypesDrawer.classList.add('open');
    });
  }

  if (finishSelector && finishDrawer) { 
    finishSelector.addEventListener('click', () => {
      if (finishField.value) { 
           finishItems.forEach(i => i.classList.remove('active'));
      document.querySelector('.fi-'+finishField.value).checked = true;
      document.querySelector('.fc-'+finishField.value).classList.add('active');
      }
      finishDrawer.show();
      finishDrawer.classList.add('open');
    });
  }

  const finishItems = document.querySelectorAll('.finish-item');

  finishItems.forEach(item => {
    item.addEventListener('click', () => {
      finishItems.forEach(i => i.classList.remove('active'));
      item.classList.add('active');
      const radio = item.querySelector('input[type="radio"]');
      if (radio) radio.checked = true;
    });
  });
const finishSubmitButtons = document.querySelectorAll('.submit-finish');

finishSubmitButtons.forEach(button => {
  button.addEventListener('click', () => {
    const selectedValue = document.querySelector('input[name="finish-selection"]:checked');

    if (selectedValue) {
      finishField.value = selectedValue.value;
      finishFieldI.textContent = selectedValue.getAttribute('data-option');

      document.querySelectorAll('.switcher-variants__group').forEach(el => {
        el.style.display = 'none';
      });

      const targetSize = document.querySelector('#size_' + selectedValue.value);
      if (targetSize) {
        targetSize.style.display = 'block';

        // Update "Attributes & quality" to match the newly selected finish
        const firstVariantRow = targetSize.querySelector('.switcher-variants__row[data-variant-id]');
        if (firstVariantRow) {
          const variantId = firstVariantRow.dataset.variantId;
          document.querySelectorAll('[data-variant-info-id]').forEach(infoEl => {
            infoEl.classList.toggle('hidden', infoEl.dataset.variantInfoId !== variantId);
          });
        }
      }

      finishDrawer.hide();
      finishDrawer.classList.remove('open');
    }
  });
});



  const urlParams = new URLSearchParams(window.location.search);
  if (!urlParams.has('variant')) {
    document.querySelectorAll('.variant-picker__option-values input:checked').forEach(d => {
      d.checked = false;
    });
  }
});


class VariantInformation extends HTMLElement {
  constructor() {
    super();
    this._onVariantChange = this._onVariantChange.bind(this);
  }

  connectedCallback() {
    document.addEventListener('variant:change', this._onVariantChange);
  }

  disconnectedCallback() {
    document.removeEventListener('variant:change', this._onVariantChange);
  }

  _onVariantChange(e) {
    // Support multiple event shapes: { detail: { variant: { id } } }, { detail: { id } }, { detail: id }
    const id = e.detail.variant.id
    if (!id) return;
    this._updateVisibility(id);
  }

  _updateVisibility(currentId) {
    document.querySelectorAll('[data-variant-info-id]').forEach((el) => {
      if (Number(el.dataset.variantInfoId) === currentId) {
        el.classList.remove('hidden');
      } else {
        el.classList.add('hidden');
      }
    });
  }
}

if (!customElements.get('variant-information')) {
  customElements.define('variant-information', VariantInformation);
}

// js/sections/header-bar.js
import { animate as animate11 } from "vendor";
var HeaderBarCarousel = class extends EffectCarousel {
  createOnChangeAnimationControls(fromSlide, toSlide) { 
    return {
      leaveControls: () => animate11(fromSlide, { opacity: [1, 0], transform: ["translateY(0)", "translateY(-10px)"] }, { duration: 0.25, easing: [0.55, 0.055, 0.675, 0.19] }),
      enterControls: () => animate11(toSlide, { opacity: [0, 1], transform: ["translateY(10px)", "translateY(0px)"] }, { duration: 0.4, easing: [0.215, 0.61, 0.355, 1] })
    };
  }
};
if (!window.customElements.get("header-bar-carousel")) {
  window.customElements.define("header-bar-carousel", HeaderBarCarousel);
}

//create drawer-color-catalog web component that loads more color entrys when scrolling to the bottom from pagination url
class DrawerColorCatalog extends HTMLElement {
  constructor() {
    super();
    this._onScroll = this._onScroll.bind(this);
    this._isLoading = false;
    this._loadedPages = new Set();
  }
  connectedCallback() {
    document.addEventListener('dialog:body-scroll', this._onScroll);
  }
  disconnectedCallback() {
    document.removeEventListener('dialog:body-scroll', this._onScroll);
  }
  _onScroll() {
    if (this._isLoading) return;
    this._loadMoreColors();
  }
  async _loadMoreColors() {
    const nextPageLink = this.querySelector('nav a');
    const nextPageUrl = nextPageLink?.href;
    if (!nextPageUrl || this._loadedPages.has(nextPageUrl)) return;

    this._isLoading = true;
    try {
      const response = await fetch(nextPageUrl);
      const data = await response.text();
      this._appendColors(data);
      this._loadedPages.add(nextPageUrl);
      // Mark the nav link as loaded
      nextPageLink.setAttribute('data-loaded', 'true');
    } catch (error) {
      console.error('Error loading more colors:', error);
    } finally {
      this._isLoading = false;
    }
  }
  _appendColors(data) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(data, 'text/html');
    const newColors = doc.querySelectorAll('.color_entry.color_entry--drawer');
    const colorContainer = this.querySelector('.color-entries');
    const lastEntry = colorContainer.lastElementChild;
    newColors.forEach(color => {
      color.setAttribute('data-loaded', 'true');
      if (lastEntry) {
      colorContainer.insertBefore(color, lastEntry.nextSibling);
      } else {
      colorContainer.appendChild(color);
      }
    });
  }
}

customElements.define('drawer-color-catalog', DrawerColorCatalog);




document.addEventListener('DOMContentLoaded', () => {
const searchInput = document.querySelector('.header-search__input');
const overlay = document.querySelector('.search-overlay');

// Add transition style to overlay
overlay.style.cssText = 'display: none; opacity: 0; transition: opacity 0.3s ease;';

searchInput.addEventListener('input', () => {
  if (searchInput.value.length > 0) {
    overlay.style.display = 'block';
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        overlay.style.opacity = '1';
      });
    });
  } else {
    overlay.style.opacity = '0';
    setTimeout(() => {
      overlay.style.display = 'none';
    }, 300);
  }
});

overlay.addEventListener('click', () => {
  searchInput.value = '';
  overlay.style.opacity = '0';
  setTimeout(() => {
    overlay.style.display = 'none';
  }, 300);
  searchInput.dispatchEvent(new Event('input', { bubbles: true }));
});
});